import { useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import type { CustomFieldDefinition } from "@platform/core";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Input,
  NativeSelect,
  Skeleton,
  Textarea,
} from "@platform/ui";
import { useConsole } from "../context";
import { useT } from "../i18n";
import { SettingErrors, useSetting } from "../lib/use-setting";
import { ErrorBanner, PageCard, TableShell, Toolbar, td, th } from "../layout/Page";

const TYPES = ["string", "number", "boolean", "date", "select"] as const;
const EMPTY: CustomFieldDefinition = {
  key: "",
  label: { th: "", en: "" },
  type: "string",
  required: false,
  showOnSignUp: false,
  includeInClaims: false,
};

export function moveItem<T>(list: T[], index: number, delta: -1 | 1): T[] {
  const j = index + delta;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[index], next[j]] = [next[j]!, next[index]!];
  return next;
}

function FieldEditor({
  initial,
  isNew,
  onSave,
  onClose,
}: {
  initial: CustomFieldDefinition;
  isNew: boolean;
  onSave: (f: CustomFieldDefinition) => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<CustomFieldDefinition>(initial);
  const [options, setOptions] = useState((initial.options ?? []).join("\n"));
  const valid =
    /^[a-z][a-zA-Z0-9_]{1,40}$/.test(f.key) &&
    f.label.th.trim() &&
    f.label.en.trim() &&
    (f.type !== "select" || options.trim());
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={isNew ? t("fields.add") : t("fields.editTitle")}
      closeLabel={t("common.close")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!valid}
            onClick={() => {
              const { options: _o, ...rest } = f;
              const opts = options
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean);
              onSave(f.type === "select" ? { ...rest, options: opts } : rest);
            }}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <Field label={t("fields.key")} required hint="^[a-z][a-zA-Z0-9_]{1,40}$">
        {(p) => (
          <Input
            {...p}
            value={f.key}
            disabled={!isNew}
            onChange={(e) => setF({ ...f, key: e.target.value })}
          />
        )}
      </Field>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field label={t("fields.labelTh")} required>
          {(p) => (
            <Input
              {...p}
              value={f.label.th}
              onChange={(e) => setF({ ...f, label: { ...f.label, th: e.target.value } })}
            />
          )}
        </Field>
        <Field label={t("fields.labelEn")} required>
          {(p) => (
            <Input
              {...p}
              value={f.label.en}
              onChange={(e) => setF({ ...f, label: { ...f.label, en: e.target.value } })}
            />
          )}
        </Field>
      </div>
      <Field label={t("fields.type")}>
        {(p) => (
          <NativeSelect
            {...p}
            value={f.type}
            onChange={(e) => setF({ ...f, type: e.target.value as CustomFieldDefinition["type"] })}
          >
            {TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {t(`fields.type.${ty}`)}
              </option>
            ))}
          </NativeSelect>
        )}
      </Field>
      {f.type === "select" && (
        <Field label={t("fields.options")} required>
          {(p) => <Textarea {...p} value={options} onChange={(e) => setOptions(e.target.value)} />}
        </Field>
      )}
      <div className="space-y-2">
        <Checkbox
          label={t("fields.required")}
          checked={f.required}
          onChange={(e) => setF({ ...f, required: e.target.checked })}
        />
        <Checkbox
          label={t("fields.showOnSignUp")}
          checked={f.showOnSignUp}
          onChange={(e) => setF({ ...f, showOnSignUp: e.target.checked })}
        />
        <Checkbox
          label={t("fields.includeInClaims")}
          checked={f.includeInClaims}
          onChange={(e) => setF({ ...f, includeInClaims: e.target.checked })}
        />
      </div>
    </Dialog>
  );
}

export function CustomFieldsPage() {
  const { t, locale } = useT();
  const { can } = useConsole();
  const s = useSetting("custom-fields");
  const [editing, setEditing] = useState<{ index: number | null; field: CustomFieldDefinition } | null>(null);
  const editable = can("settings:write");
  const fields = s.draft ?? [];
  const dirty = s.query.data && JSON.stringify(s.query.data.value) !== JSON.stringify(fields);
  return (
    <PageCard>
      <Toolbar
        actions={
          editable && (
            <>
              <Button variant="secondary" onClick={() => setEditing({ index: null, field: EMPTY })}>
                <Plus className="size-4" aria-hidden />
                {t("fields.add")}
              </Button>
              <Button
                data-testid="save-settings-button"
                disabled={!dirty}
                loading={s.save.isPending}
                onClick={() => s.save.mutate(fields)}
              >
                {t("fields.save")}
              </Button>
            </>
          )
        }
        onReload={() => void s.reload()}
      />
      <SettingErrors state={s} conflictLabel={t("signin.conflict")} />
      {s.query.isError ? (
        <ErrorBanner error={s.query.error} onRetry={() => void s.query.refetch()} />
      ) : !s.draft ? (
        <Skeleton className="m-4 h-32" />
      ) : fields.length === 0 ? (
        <EmptyState title={t("fields.empty")} />
      ) : (
        <TableShell label={t("nav.customFields")}>
          <thead>
            <tr>
              <th className={th}>{t("fields.key")}</th>
              <th className={th}>{t("fields.type")}</th>
              <th className={th} />
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {fields.map((f, i) => (
              <tr key={f.key}>
                <td className={td}>
                  <p className="font-medium">{f.label[locale]}</p>
                  <code className="text-xs text-ink-2">{f.key}</code>
                </td>
                <td className={td}>{t(`fields.type.${f.type}`)}</td>
                <td className={td}>
                  <div className="flex flex-wrap gap-1">
                    {f.required && <Badge tone="warning">{t("fields.required")}</Badge>}
                    {f.showOnSignUp && <Badge>{t("fields.showOnSignUp")}</Badge>}
                    {f.includeInClaims && <Badge tone="info">claims</Badge>}
                  </div>
                </td>
                <td className={`${td} whitespace-nowrap text-right`}>
                  {editable && (
                    <>
                      <IconButton
                        label={t("common.moveUp")}
                        disabled={i === 0}
                        onClick={() => s.setDraft(moveItem(fields, i, -1))}
                      >
                        <ArrowUp className="size-4" aria-hidden />
                      </IconButton>
                      <IconButton
                        label={t("common.moveDown")}
                        disabled={i === fields.length - 1}
                        onClick={() => s.setDraft(moveItem(fields, i, 1))}
                      >
                        <ArrowDown className="size-4" aria-hidden />
                      </IconButton>
                      <IconButton label={t("common.edit")} onClick={() => setEditing({ index: i, field: f })}>
                        <Pencil className="size-4" aria-hidden />
                      </IconButton>
                      <IconButton
                        label={t("common.delete")}
                        onClick={() => s.setDraft(fields.filter((_, j) => j !== i))}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </IconButton>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}
      {editing && (
        <FieldEditor
          initial={editing.field}
          isNew={editing.index === null}
          onClose={() => setEditing(null)}
          onSave={(f) => {
            s.setDraft(
              editing.index === null ? [...fields, f] : fields.map((x, j) => (j === editing.index ? f : x)),
            );
            setEditing(null);
          }}
        />
      )}
    </PageCard>
  );
}
